import React, { useState, useEffect, useRef } from 'react';
import { KeyRound, ArrowLeft, CheckCircle, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../config';
import './OtpVerification.css';

function ResetPassword() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [status, setStatus] = useState('idle'); // idle, verifying, success, error
  const [message, setMessage] = useState('');

  // Resend OTP Cooldown
  const [cooldown, setCooldown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputsRef = useRef([]);

  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_\-+=~`\[\]\\;'/]).{8,}$/;

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1]);
    const urlEmail = params.get('email');
    if (urlEmail) setEmail(urlEmail);
    else {
      setStatus('error');
      setMessage('Missing email address. Please request a new password reset from the login screen.');
    }
  }, []);

  useEffect(() => {
    let timer;
    if (cooldown > 0 && !canResend) {
      timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    } else if (cooldown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [cooldown, canResend]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-advance
    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      const focusIndex = pastedData.length < 6 ? pastedData.length : 5;
      inputsRef.current[focusIndex].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = otp.join('');
    if (token.length !== 6) {
      setStatus('error');
      setMessage('Please enter all 6 digits of the verification code.');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }
    if (!passwordRegex.test(password)) {
      setStatus('error');
      setMessage('Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character.');
      return;
    }

    setStatus('verifying');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, password })
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message);
      } else {
        setStatus('error');
        setMessage(data.message || 'Password reset failed.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Network error during password reset.');
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCanResend(false);
    setCooldown(60);
    setStatus('idle');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('A new code has been sent to your email.');
      } else {
        setMessage(data.message || 'Failed to resend code.');
      }
    } catch (err) {
      setMessage('Network error resending code.');
    }
  };

  return (
    <div className="otp-page-wrapper">
      <div className="otp-card">
        <div className="otp-icon-badge">
          <KeyRound size={28} />
        </div>

        <h2 className="otp-title">Reset Password</h2>
        <p className="otp-subtitle">
          Enter the 6-digit verification code sent to <br />
          <span className="otp-email-highlight">{email}</span>
        </p>

        {status === 'success' ? (
          <div style={{ textAlign: 'center', animation: 'otpCardScaleUp 0.3s ease-out' }}>
            <div className="otp-status-success" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <CheckCircle size={18} />
              <span>{message}</span>
            </div>
            <button 
              className="otp-submit-btn"
              onClick={() => window.location.href = '#/'} 
              style={{ marginTop: '1.25rem' }}
            >
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <div className="otp-inputs-group" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`otp-input-field ${digit ? 'filled' : ''}`}
                />
              ))}
            </div>

            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '1.25rem' }}>
              <input
                type="password"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="form-control"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1.5px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(15, 23, 42, 0.6)',
                  color: '#ffffff',
                  boxSizing: 'border-box'
                }}
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="form-control"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1.5px solid rgba(255, 255, 255, 0.12)',
                  background: 'rgba(15, 23, 42, 0.6)',
                  color: '#ffffff',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {status === 'error' && <div className="otp-status-error">{message}</div>}
            {status === 'idle' && message && <div className="otp-status-info">{message}</div>}

            <button 
              type="submit" 
              disabled={status === 'verifying'}
              className="otp-submit-btn"
            >
              {status === 'verifying' ? (
                <>
                  <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Resetting Password…
                </>
              ) : (
                'Reset Password'
              )}
            </button>

            <div className="otp-footer-links" style={{ width: '100%' }}>
              <a href="#/" className="otp-link-back">
                <ArrowLeft size={15} /> Back to Login
              </a>
              <button 
                type="button" 
                onClick={handleResend}
                disabled={!canResend}
                className="otp-resend-btn"
              >
                {canResend ? 'Resend Code' : `Resend in ${cooldown}s`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
